import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiTruck, FiTool, FiDollarSign, FiEye, FiX, FiCreditCard, FiCalendar, FiCheck, FiSquare } from "react-icons/fi";
import { title } from "../utils/style";
import fetchData from "../custom_functions/fetchData";
import postData from "../custom_functions/postData";
import { url } from "../utils/config";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
});

function formatCurrency(value) {
    const numeric = Number(value);
    return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
}

function formatDate(value) {
    if (!value) return "-";
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return new Intl.DateTimeFormat("en-US", {
            dateStyle: "medium",
        }).format(date);
    } catch (error) {
        return value;
    }
}

function extractVehicleInfo(quote) {
    const collections = [];
    if (Array.isArray(quote?.fleet_customer_cars)) collections.push(quote.fleet_customer_cars);
    if (Array.isArray(quote?.vehicles)) collections.push(quote.vehicles);
    if (quote?.fleet_customer_car) collections.push([quote.fleet_customer_car]);
    if (quote?.car) collections.push([quote.car]);
    const flattened = collections.flat();

    if (flattened.length === 0) return { make: "-", model: "-", year: "-" };

    const vehicle = flattened[0];
    return {
        make: vehicle?.make || vehicle?.vehicle_make || "-",
        model: vehicle?.model || vehicle?.vehicle_model || "-",
        year: vehicle?.year || vehicle?.manufacture_year || "-",
    };
}

function deriveServiceCount(quote) {
    if (Array.isArray(quote?.serviceList)) return quote.serviceList.length;
    if (Array.isArray(quote?.services)) return quote.services.length;
    return 0;
}

function deriveCustomServiceCount(quote) {
    if (Array.isArray(quote?.customList)) return quote.customList.length;
    if (Array.isArray(quote?.customServices)) return quote.customServices.length;
    if (Array.isArray(quote?.custom_service_list)) return quote.custom_service_list.length;
    return 0;
}

function extractServiceList(quote) {
    const services = Array.isArray(quote?.serviceList) ? quote.serviceList : [];
    return services
        .map((service, index) => {
            if (!service) return null;
            const name = service?.name || `Service ${index + 1}`;
            const price = service?.cost ?? service?.price;
            return {
                id: service?.id || service?.service_id || service?.code || `${name}-${index}`,
                name,
                price: Number(price) || 0,
            };
        })
        .filter(Boolean);
}

function extractCustomServiceList(quote) {
    const customServices = Array.isArray(quote?.customList) ? quote.customList : [];
    return customServices
        .map((service, index) => {
            if (!service) return null;
            const name = service?.itemName || service?.name || `Custom Service ${index + 1}`;
            const price = service?.cost;
            return {
                id: service?.id || `${name}-${index}`,
                name,
                price: Number(price) || 0,
            };
        })
        .filter(Boolean);
}

function extractBundleList(quote) {
    const bundles = Array.isArray(quote?.bundleList) ? quote.bundleList : [];
    return bundles
        .map((bundle, index) => {
            if (!bundle) return null;
            const name = bundle?.name || `Bundle ${index + 1}`;
            const cost = bundle?.cost ?? bundle?.price ?? 0;
            return {
                id: bundle?.id || `bundle-${index}`,
                name,
                cost: Number(cost) || 0,
                // Include tire metadata if present
                tireCost: bundle?.tireCost,
                tireType: bundle?.tireType,
                tireQuantity: bundle?.tireQuantity,
                tireNotes: bundle?.tireNotes,
            };
        })
        .filter(Boolean);
}

function extractMaterialList(quote) {
    const materials = Array.isArray(quote?.materialList) ? quote.materialList : [];
    return materials
        .map((material, index) => {
            if (!material) return null;
            const name = material?.name || `Part ${index + 1}`;
            const cost = material?.cost ?? material?.price ?? 0;
            const labour = material?.labour ?? 0;
            return {
                id: material?.id || `material-${index}`,
                name,
                cost: Number(cost) || 0,
                labour: Number(labour) || 0,
                estimatedTime: material?.estimatedTime || 0,
            };
        })
        .filter(Boolean);
}

function extractTireList(quote) {
    const tires = Array.isArray(quote?.tireList) ? quote.tireList : [];
    return tires
        .map((tire, index) => {
            if (!tire) return null;
            return {
                id: tire?.id || `tire-${index}`,
                name: tire?.name || "Tire",
                quantity: Number(tire?.quantity) || 0,
                price: Number(tire?.cost) || 0,
                notes: tire?.notes || "",
            };
        })
        .filter(Boolean);
}

function Billing() {
    const [quotes, setQuotes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [isNextPage, setIsNextPage] = useState(false);
    const [paymentLoadingQuoteId, setPaymentLoadingQuoteId] = useState(null);
    const [reloadToggle, setReloadToggle] = useState(false);
    const [viewDetailsQuote, setViewDetailsQuote] = useState(null);
    const [invoiceDetails, setInvoiceDetails] = useState({
        totalSpent: 0,
        transactionCount: 0,
        lastPaymentDate: null,
    });
    const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);
    const [selectedQuoteIds, setSelectedQuoteIds] = useState([]);
    const [isBulkPaymentLoading, setIsBulkPaymentLoading] = useState(false);

    const hasQuotes = quotes.length > 0;
    const hasSelectedQuotes = selectedQuoteIds.length > 0;
    const allSelected = hasQuotes && selectedQuoteIds.length === quotes.length;
    const canGoBack = page > 1;
    const canGoForward = Boolean(isNextPage);

    useEffect(() => {
        fetchApprovedQuotations(page);
    }, [page, reloadToggle]);

    useEffect(() => {
        fetchInvoiceDetails();
    }, [reloadToggle]);

    useEffect(() => {
        let lastVisibilityTime = Date.now();

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                const now = Date.now();
                if (now - lastVisibilityTime > 1000) {
                    setSelectedQuoteIds([]);
                    setReloadToggle((prev) => !prev);
                }
            } else {
                lastVisibilityTime = Date.now();
            }
        };

        const handlePageShow = (event) => {
            if (event.persisted) {
                setSelectedQuoteIds([]);
                setReloadToggle((prev) => !prev);
            }
        };

        // Also handle when user returns via browser back/forward
        const handleFocus = () => {
            setSelectedQuoteIds([]);
            setReloadToggle((prev) => !prev);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pageshow", handlePageShow);
        window.addEventListener("focus", handleFocus);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pageshow", handlePageShow);
            window.removeEventListener("focus", handleFocus);
        };
    }, []);

    function fetchApprovedQuotations(pageNumber = 1) {
        setIsLoading(true);
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        fetchData(
            `${url}/billing?${params.toString()}`,
            (error) => {
                setIsLoading(false);
                toast.error(error?.response?.data?.message || "Unable to load billing data", { autoClose: 3000 });
            },
            (response) => {
                setQuotes(response?.data || []);
                setIsNextPage(Boolean(response?.isNextPage));
                setIsLoading(false);
            },
            false
        );
    }

    function fetchInvoiceDetails() {
        setIsInvoiceLoading(true);
        fetchData(
            `${url}/invoice_details`,
            (error) => {
                setIsInvoiceLoading(false);
                console.error("Failed to load invoice details:", error);
            },
            (response) => {
                setInvoiceDetails({
                    totalSpent: response?.totalSpent || 0,
                    transactionCount: response?.transactionCount || 0,
                    lastPaymentDate: response?.lastPaymentDate || null,
                });
                setIsInvoiceLoading(false);
            },
            false
        );
    }

    function handlePayNow(quoteId) {
        if (!quoteId || paymentLoadingQuoteId) return;
        setPaymentLoadingQuoteId(quoteId);
        fetchData(
            `${url}/service_req_payment/${quoteId}`,
            (error) => {
                setPaymentLoadingQuoteId(null);
                toast.error(error?.response?.data?.message || "Failed to get payment link", { autoClose: 3000 });
            },
            (response) => {
                setPaymentLoadingQuoteId(null);
                if (response?.paymentLink) {
                    window.location.href = response.paymentLink;
                } else {
                    toast.error("Payment link not available", { autoClose: 3000 });
                }
            },
            false
        );
    }

    function goToPreviousPage() {
        if (!canGoBack) return;
        setPage((prev) => Math.max(prev - 1, 1));
    }

    function goToNextPage() {
        if (!canGoForward) return;
        setPage((prev) => prev + 1);
    }

    function toggleQuoteSelection(quoteId) {
        setSelectedQuoteIds((prev) =>
            prev.includes(quoteId)
                ? prev.filter((id) => id !== quoteId)
                : [...prev, quoteId]
        );
    }

    function toggleSelectAll() {
        if (allSelected) {
            setSelectedQuoteIds([]);
        } else {
            setSelectedQuoteIds(quotes.map((q) => q.id));
        }
    }

    function handleBulkPayment() {
        if (!hasSelectedQuotes || isBulkPaymentLoading) return;
        setIsBulkPaymentLoading(true);
        postData(
            `${url}/service_req_payment/bulk`,
            { serviceIds: selectedQuoteIds },
            (error) => {
                setIsBulkPaymentLoading(false);
                toast.error(error?.response?.data?.message || "Failed to process bulk payment", { autoClose: 3000 });
            },
            (response) => {
                setIsBulkPaymentLoading(false);
                if (response?.paymentLink) {
                    window.location.href = response.paymentLink;
                } else {
                    toast.success(response?.message || "Bulk payment processed successfully", { autoClose: 3000 });
                    setSelectedQuoteIds([]);
                    setReloadToggle((prev) => !prev);
                }
            }
        );
    }

    // Calculate total for selected quotes
    const selectedTotal = quotes
        .filter((q) => selectedQuoteIds.includes(q.id))
        .reduce((sum, q) => sum + (Number(q.totalCost) || 0), 0);

    return (
        <div className="p-4" style={{ backgroundColor: "#e6f9ec", borderRadius: "12px" }}>
            <div className="mb-4">
                <div className={title}>Billing</div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                {/* Total Spent */}
                <div className="rounded-2xl border border-emerald-100 bg-white p-6 shadow-[0_12px_30px_rgba(22,101,52,0.12)]">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                            <FiDollarSign size={24} />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-500 mb-1">Total Spent</div>
                            <div className="text-2xl font-bold text-slate-900">
                                {isInvoiceLoading ? "..." : formatCurrency(invoiceDetails.totalSpent)}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">Lifetime payments</div>
                        </div>
                    </div>
                </div>

                {/* Total Transactions */}
                <div className="rounded-2xl border border-blue-100 bg-white p-6 shadow-[0_12px_30px_rgba(59,130,246,0.12)]">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                            <FiCreditCard size={24} />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-blue-500 mb-1">Total Transactions</div>
                            <div className="text-2xl font-bold text-slate-900">
                                {isInvoiceLoading ? "..." : invoiceDetails.transactionCount}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">Total payments</div>
                        </div>
                    </div>
                </div>

                {/* Last Payment Date */}
                <div className="rounded-2xl border border-purple-100 bg-white p-6 shadow-[0_12px_30px_rgba(168,85,247,0.12)]">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 text-purple-600">
                            <FiCalendar size={24} />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-purple-500 mb-1">Last Payment</div>
                            <div className="text-2xl font-bold text-slate-900">
                                {isInvoiceLoading ? "..." : formatDate(invoiceDetails.lastPaymentDate)}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">Most recent</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Header */}
            <h2 className="text-lg font-bold text-emerald-900 mb-3">Unpaid services</h2>

            {/* Select All and Pagination */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                {!isLoading && hasQuotes && (
                    <button
                        type="button"
                        onClick={toggleSelectAll}
                        className="flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                    >
                        <span className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${allSelected ? "bg-emerald-600 border-emerald-600 text-white" : "border-emerald-300 bg-white"}`}>
                            {allSelected && <FiCheck size={12} />}
                        </span>
                        <span>{allSelected ? "Deselect All" : "Select All"}</span>
                    </button>
                )}
                {!isLoading && hasQuotes && (
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-emerald-600">Page {page}</span>
                        <button
                            type="button"
                            onClick={goToPreviousPage}
                            disabled={!canGoBack}
                            className={`px-4 py-1.5 rounded-full border text-sm font-semibold transition-colors ${canGoBack
                                ? "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                                }`}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            onClick={goToNextPage}
                            disabled={!canGoForward}
                            className={`px-4 py-1.5 rounded-full border text-sm font-semibold transition-colors ${canGoForward
                                ? "border-emerald-500 bg-emerald-600 text-white shadow-[0_10px_25px_rgba(16,185,129,0.25)] hover:bg-emerald-700"
                                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                                }`}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4">
                {isLoading && (
                    <div className="rounded-2xl border border-emerald-100 bg-white px-6 py-12 text-center text-emerald-600 shadow-[0_18px_40px_rgba(22,101,52,0.12)]">
                        Loading billing data...
                    </div>
                )}

                {!isLoading && !hasQuotes && (
                    <div className="rounded-2xl border border-dashed border-emerald-200 bg-white px-6 py-16 text-center text-emerald-500 shadow-[0_12px_30px_rgba(22,101,52,0.12)]">
                        No service requests found.
                    </div>
                )}

                {!isLoading && hasQuotes && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {quotes.map((quote) => {
                            const quoteId = quote?.id || "Quotation";
                            const vehicleInfo = extractVehicleInfo(quote);
                            const serviceCount = deriveServiceCount(quote);
                            const customServiceCount = deriveCustomServiceCount(quote);
                            const isSelected = selectedQuoteIds.includes(quoteId);

                            return (
                                <div
                                    key={quoteId}
                                    className={`rounded-2xl border bg-white p-5 shadow-[0_12px_30px_rgba(22,101,52,0.12)] transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(22,101,52,0.18)] ${isSelected ? "border-emerald-500 ring-2 ring-emerald-200" : "border-emerald-100"}`}
                                >
                                    {/* Selection Checkbox and Vehicle Details */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={() => toggleQuoteSelection(quoteId)}
                                                className={`flex h-6 w-6 items-center justify-center rounded border-2 transition-colors ${isSelected ? "bg-emerald-600 border-emerald-600 text-white" : "border-emerald-300 bg-white hover:border-emerald-400"}`}
                                            >
                                                {isSelected && <FiCheck size={14} />}
                                            </button>
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                                                <FiTruck size={20} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-bold text-emerald-900">
                                                    {vehicleInfo.make} {vehicleInfo.model}
                                                </div>
                                                <div className="text-xs text-emerald-600">{vehicleInfo.year}</div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setViewDetailsQuote(quote)}
                                            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700 transition-colors"
                                            title="View service details"
                                        >
                                            <FiEye size={16} />
                                        </button>
                                    </div>

                                    {/* Service Counts */}
                                    <div className="flex items-center gap-4 mb-4 pb-4 border-b border-emerald-100">
                                        <div className="flex items-center gap-2">
                                            <FiTool size={14} className="text-emerald-500" />
                                            <span className="text-xs text-slate-600">
                                                <span className="font-semibold text-emerald-700">{serviceCount}</span> Service(s)
                                            </span>
                                        </div>
                                        <div className="h-4 w-px bg-emerald-200" />
                                        <div className="flex items-center gap-2">
                                            <FiTool size={14} className="text-blue-500" />
                                            <span className="text-xs text-slate-600">
                                                <span className="font-semibold text-blue-700">{customServiceCount}</span> Custom
                                            </span>
                                        </div>
                                    </div>

                                    {/* Total Amount */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-semibold uppercase tracking-wide text-emerald-500">Total</span>
                                        </div>
                                        <span className="text-xl font-bold text-slate-900">{formatCurrency(quote?.totalCost)}</span>
                                    </div>

                                    {/* Due Date */}
                                    {quote?.dueDate && (
                                        <div className="flex items-center justify-between mb-4 pb-4 border-b border-emerald-100">
                                            <div className="flex items-center gap-2">
                                                <FiCalendar size={16} className="text-amber-600" />
                                                <span className="text-xs font-semibold uppercase tracking-wide text-amber-500">Due Date</span>
                                            </div>
                                            <span className="text-sm font-semibold text-amber-700">{formatDate(quote?.dueDate)}</span>
                                        </div>
                                    )}

                                    {/* Pay Now Button */}
                                    <button
                                        type="button"
                                        onClick={() => handlePayNow(quoteId)}
                                        disabled={paymentLoadingQuoteId === quoteId}
                                        className={`w-full rounded-full py-2.5 text-sm font-semibold transition-colors ${paymentLoadingQuoteId === quoteId
                                            ? "bg-emerald-300 text-white cursor-not-allowed"
                                            : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,0.25)]"
                                            }`}
                                    >
                                        {paymentLoadingQuoteId === quoteId ? "Loading..." : "Pay Now"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Service Details Modal */}
            {viewDetailsQuote && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
                        <button
                            type="button"
                            onClick={() => setViewDetailsQuote(null)}
                            className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                            <FiX size={18} />
                        </button>

                        <h3 className="text-lg font-bold text-emerald-900 mb-4">Service Details</h3>

                        {/* Vehicle Info */}
                        <div className="mb-4 p-3 rounded-xl bg-emerald-50">
                            <div className="flex items-center gap-2 mb-1">
                                <FiTruck size={14} className="text-emerald-600" />
                                <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Vehicle</span>
                            </div>
                            <div className="text-sm font-bold text-emerald-900">
                                {extractVehicleInfo(viewDetailsQuote).make} {extractVehicleInfo(viewDetailsQuote).model} ({extractVehicleInfo(viewDetailsQuote).year})
                            </div>
                        </div>

                        {/* Services List */}
                        {extractServiceList(viewDetailsQuote).length > 0 && (
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <FiTool size={14} className="text-emerald-600" />
                                    <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Services</span>
                                </div>
                                <ul className="space-y-2">
                                    {extractServiceList(viewDetailsQuote).map((service) => (
                                        <li
                                            key={service.id}
                                            className="flex items-center justify-between rounded-lg border border-emerald-100 px-3 py-2 text-xs"
                                        >
                                            <span className="font-medium text-slate-700">{service.name}</span>
                                            <span className="font-semibold text-emerald-700">{formatCurrency(service.price)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Service Bundles List */}
                        {extractBundleList(viewDetailsQuote).length > 0 && (
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <FiTool size={14} className="text-blue-600" />
                                    <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">Service Bundles</span>
                                </div>
                                <ul className="space-y-2">
                                    {extractBundleList(viewDetailsQuote).map((bundle) => (
                                        <li
                                            key={bundle.id}
                                            className="rounded-lg border border-blue-100 px-3 py-2 text-xs"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium text-slate-700">{bundle.name}</span>
                                                <span className="font-semibold text-blue-700">{formatCurrency(bundle.cost)}</span>
                                            </div>
                                            {/* Tire Details - shown if bundle has tire information */}
                                            {(bundle?.tireCost !== undefined && bundle?.tireCost !== null) && (
                                                <div className="mt-2 pt-2 border-t border-blue-50">
                                                    <div className="flex items-center gap-1 mb-1">
                                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-500">Tire Details</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                                                        {bundle?.tireType && (
                                                            <div className="flex justify-between">
                                                                <span className="text-slate-500">Type:</span>
                                                                <span className="font-medium text-slate-700">{bundle.tireType}</span>
                                                            </div>
                                                        )}
                                                        {bundle?.tireQuantity !== undefined && (
                                                            <div className="flex justify-between">
                                                                <span className="text-slate-500">Quantity:</span>
                                                                <span className="font-medium text-slate-700">{bundle.tireQuantity}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex justify-between">
                                                            <span className="text-slate-500">Price Per Tire:</span>
                                                            <span className="font-medium text-blue-600">{formatCurrency(bundle.tireCost)}</span>
                                                        </div>
                                                        {bundle?.tireNotes && (
                                                            <div className="col-span-2 flex gap-1">
                                                                <span className="text-slate-500">Notes:</span>
                                                                <span className="font-medium text-slate-700">{bundle.tireNotes}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Parts / Materials List */}
                        {extractMaterialList(viewDetailsQuote).length > 0 && (
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <FiTool size={14} className="text-amber-600" />
                                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-600">Parts / Materials</span>
                                </div>
                                <ul className="space-y-2">
                                    {extractMaterialList(viewDetailsQuote).map((material) => (
                                        <li
                                            key={material.id}
                                            className="flex items-center justify-between rounded-lg border border-amber-100 px-3 py-2 text-xs"
                                        >
                                            <div>
                                                <span className="font-medium text-slate-700">{material.name}</span>
                                                {material.estimatedTime > 0 && (
                                                    <span className="ml-2 text-amber-600">({material.estimatedTime} min)</span>
                                                )}
                                            </div>
                                            <span className="font-semibold text-amber-700">{formatCurrency(material.cost + material.labour)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Custom Services List */}
                        {extractCustomServiceList(viewDetailsQuote).length > 0 && (
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <FiTool size={14} className="text-purple-600" />
                                    <span className="text-xs font-semibold uppercase tracking-wide text-purple-600">Custom Services</span>
                                </div>
                                <ul className="space-y-2">
                                    {extractCustomServiceList(viewDetailsQuote).map((service) => (
                                        <li
                                            key={service.id}
                                            className="flex items-center justify-between rounded-lg border border-dashed border-purple-200 px-3 py-2 text-xs"
                                        >
                                            <span className="font-medium text-slate-700">{service.name}</span>
                                            <span className="font-semibold text-purple-700">{formatCurrency(service.price)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Tires List */}
                        {extractTireList(viewDetailsQuote).length > 0 && (
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <FiTool size={14} className="text-violet-600" />
                                    <span className="text-xs font-semibold uppercase tracking-wide text-violet-600">Tires</span>
                                </div>
                                <ul className="space-y-2">
                                    {extractTireList(viewDetailsQuote).map((tire) => (
                                        <li
                                            key={tire.id}
                                            className="flex items-center justify-between rounded-lg border border-violet-100 px-3 py-2 text-xs"
                                        >
                                            <div>
                                                <span className="font-medium text-slate-700">{tire.name} x {tire.quantity}</span>
                                                {tire.notes && (
                                                    <span className="ml-2 text-violet-600">({tire.notes})</span>
                                                )}
                                            </div>
                                            <span className="font-semibold text-violet-700">{formatCurrency(tire.price * tire.quantity)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Cost Breakdown */}
                        <div className="mb-4 p-3 rounded-xl bg-slate-50 space-y-2">
                            {(viewDetailsQuote?.technicianCost > 0 || viewDetailsQuote?.materialCost > 0) && (
                                <>
                                    {viewDetailsQuote?.technicianCost > 0 && (
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-slate-600">Technician Cost</span>
                                            <span className="font-medium text-slate-700">{formatCurrency(viewDetailsQuote.technicianCost)}</span>
                                        </div>
                                    )}
                                    {viewDetailsQuote?.materialCost > 0 && (
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-slate-600">Material Cost</span>
                                            <span className="font-medium text-slate-700">{formatCurrency(viewDetailsQuote.materialCost)}</span>
                                        </div>
                                    )}
                                </>
                            )}
                            {viewDetailsQuote?.tax > 0 && (
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-600">
                                        Tax {viewDetailsQuote?.taxRatePercent ? `(${viewDetailsQuote.taxRatePercent}%)` : ""}
                                    </span>
                                    <span className="font-medium text-slate-700">{formatCurrency(viewDetailsQuote.tax)}</span>
                                </div>
                            )}
                        </div>

                        {/* Total */}
                        <div className="flex items-center justify-between pt-3 border-t border-emerald-100">
                            <span className="text-sm font-semibold text-slate-600">Total Amount</span>
                            <span className="text-xl font-bold text-emerald-700">{formatCurrency(viewDetailsQuote?.totalCost)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Payment Floating Bar */}
            {hasSelectedQuotes && (
                <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-emerald-200 shadow-[0_-8px_30px_rgba(22,101,52,0.15)] p-4">
                    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm">
                                    {selectedQuoteIds.length}
                                </span>
                                <span className="text-sm font-medium text-slate-600">
                                    {selectedQuoteIds.length === 1 ? "quote selected" : "quotes selected"}
                                </span>
                            </div>
                            <div className="h-6 w-px bg-emerald-200" />
                            <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-slate-900">{formatCurrency(selectedTotal)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setSelectedQuoteIds([])}
                                className="px-5 py-2.5 rounded-full border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                            >
                                Clear Selection
                            </button>
                            <button
                                type="button"
                                onClick={handleBulkPayment}
                                disabled={isBulkPaymentLoading}
                                className={`px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-colors ${
                                    isBulkPaymentLoading
                                        ? "bg-emerald-300 cursor-not-allowed"
                                        : "bg-emerald-600 hover:bg-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,0.25)]"
                                }`}
                            >
                                {isBulkPaymentLoading ? "Processing..." : `Pay ${selectedQuoteIds.length} Selected`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Billing;
